import { useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Copy, Check, PlayCircle, ExternalLink } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { eventConfig } from "@/config/eventConfig";
import { buildShareMessage, whatsappShareUrl } from "@/lib/shareMessage";
import { PANEL_CLASS } from "./dashboardUtils";

const TEAM_CODE = "ac01"; // Affinity Core Team default

const WA_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-[#1F9188] text-white text-sm font-semibold hover:bg-[#126F6A] transition-colors";
const GHOST_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl border border-[rgba(17,42,67,0.15)] bg-white text-sm font-medium text-[#112A43] hover:bg-[#FBF8F0] transition-colors";

/** Copies text; returns true on success. */
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * "Share the video" card: pick an adviser, preview the exact WhatsApp message
 * (90-sec promo + that adviser's referral link) and send it in one tap.
 */
export const VideoShareCard = () => {
  const advisers = eventConfig.advisers || [];
  const [code, setCode] = useState(advisers.some((a) => a.code === TEAM_CODE) ? TEAM_CODE : advisers[0]?.code || "");
  const [copied, setCopied] = useState(false);
  const { video } = eventConfig;

  const message = useMemo(() => buildShareMessage(code), [code]);
  const adviser = advisers.find((a) => a.code === code);

  const onCopy = async () => {
    if (await copyText(message)) {
      setCopied(true);
      toast.success("Message copied. Paste it into any chat.");
      setTimeout(() => setCopied(false), 1600);
    } else {
      toast.error("Copy failed \u2014 select the preview text and copy manually.");
    }
  };

  return (
    <div className={PANEL_CLASS} data-testid="video-share-card">
      <div className="p-5 border-b border-[rgba(17,42,67,0.08)] flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#122131]">Share the 90-sec Video</h2>
          <p className="text-sm text-[#647384] mt-1">
            One tap sends the promo video plus your personal referral link, so every sign-up it brings is tagged to you.
          </p>
        </div>
        <a
          href={video.shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="video-share-preview-link"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#126F6A] hover:underline"
        >
          <PlayCircle size={16} /> Preview video <ExternalLink size={13} />
        </a>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-5">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#122131] mb-2" htmlFor="video-share-adviser">
              Send as
            </label>
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger id="video-share-adviser" data-testid="video-share-adviser-select" className="h-11 rounded-xl bg-white">
                <SelectValue placeholder="Choose an adviser" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {advisers.map((a) => (
                  <SelectItem key={a.code} value={a.code} data-testid={`video-share-adviser-${a.code}`}>
                    {a.name} <span className="text-[#647384]">&middot; {a.code}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-2 text-xs text-[#647384] break-all" data-testid="video-share-link">
              Link in message: <span className="font-medium text-[#122131]">{buildShareMessage(code).match(/https?:\/\/\S+ref=\S+/)?.[0] || ""}</span>
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <a
              href={whatsappShareUrl(message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="video-share-whatsapp"
              className={WA_BUTTON_CLASS}
            >
              <MessageCircle size={17} /> Send via WhatsApp
            </a>
            <button type="button" onClick={onCopy} data-testid="video-share-copy" className={GHOST_BUTTON_CLASS}>
              {copied ? <Check size={16} className="text-[#126F6A]" /> : <Copy size={16} />} {copied ? "Copied" : "Copy message"}
            </button>
          </div>
          <p className="text-xs text-[#647384]">
            WhatsApp opens with the message ready &mdash; just choose the contact or group. Works on phone and WhatsApp Web.
          </p>
        </div>

        {/* Chat-bubble preview */}
        <div className="rounded-2xl p-4 bg-[#ECE5DD]" aria-label="Message preview">
          <div className="text-[11px] text-[#647384] mb-2">Preview &middot; {adviser ? adviser.name : ""}</div>
          <div
            data-testid="video-share-preview"
            className="relative ml-auto max-w-[95%] rounded-2xl rounded-tr-sm bg-[#DCF8C6] px-3.5 py-2.5 text-[13px] leading-relaxed text-[#122131] whitespace-pre-wrap break-words shadow-[0_1px_1px_rgba(0,0,0,0.08)]"
          >
            {message}
          </div>
        </div>
      </div>
    </div>
  );
};
